import { type FormEvent, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useCreateKnowledgeBase } from "@/hooks/use-knowledge-base";
import { failureMessage, type KnowledgeBase } from "@/services/knowledge-base.service";
import { knowledgeBaseNameError } from "@/utils/kb-upload-rules";

interface CreateKnowledgeBaseDialogProps {
  workspaceId: string;
  /** The new knowledge base, to open. */
  onCreated: (kb: KnowledgeBase) => void;
}

/**
 * Names a new knowledge base. The name is checked as the function checks it, from the first press of
 * Create on; a plan with no room for another refuses it, and its sentence shows here.
 */
export function CreateKnowledgeBaseDialog({ workspaceId, onCreated }: CreateKnowledgeBaseDialogProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [refusal, setRefusal] = useState<string>();
  const create = useCreateKnowledgeBase(workspaceId);
  const { toast } = useToast();
  const nameError = submitted ? knowledgeBaseNameError(name) : null;

  const openChange = (next: boolean) => {
    if (create.isPending) return;
    setOpen(next);
    setName("");
    setSubmitted(false);
    setRefusal(undefined);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (knowledgeBaseNameError(name)) return;
    setRefusal(undefined);
    try {
      const kb = await create.mutateAsync(name.trim());
      toast({ title: "Knowledge base created", description: "Upload files to it for your agents to search." });
      setOpen(false);
      onCreated(kb);
    } catch (error) {
      setRefusal(failureMessage(error, "The knowledge base could not be created. Try again in a moment."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={openChange}>
      <DialogTrigger asChild>
        <Button className="shadow-md">
          <Plus className="mr-2 h-4 w-4" />
          New knowledge base
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New knowledge base</DialogTitle>
          <DialogDescription>Give it a name. You upload its files next.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="kb-name">Name</Label>
            <Input
              id="kb-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Product documentation"
              aria-invalid={nameError ? true : undefined}
              aria-describedby={nameError ? "kb-name-error" : undefined}
            />
            {nameError && (
              <p id="kb-name-error" className="text-sm text-destructive">
                {nameError}
              </p>
            )}
          </div>
          {refusal && (
            <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {refusal}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => openChange(false)} disabled={create.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending} aria-busy={create.isPending}>
              {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {create.isPending ? "Creating…" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
